import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './Software.css';

const LicenseGrid = ({ skuFilter = null }) => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [rows, setRows] = useState([]); // flat successful assignments from API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedSku, setExpandedSku] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTenant) return;
      try {
        setLoading(true);
        setError(null);
        const url = new URL(`${getApiBaseUrl()}/api/license-assignments`);
        url.searchParams.set('tenant', selectedTenant.name);
        const resp = await authenticatedFetch(url.toString());
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          setError(data.error || 'Failed to fetch license assignments');
        } else {
          setRows(data.assignments || []);
        }
      } catch (e) {
        console.error('Error fetching license assignments:', e);
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedTenant, skuFilter, authenticatedFetch]);

  // Pre-fill search with incoming SKU filter (must be declared before any returns)
  useEffect(() => {
    if (skuFilter && !search) {
      setSearch(skuFilter);
    }
  }, [skuFilter, search]);

  // Do not auto-expand any row by default

  if (!selectedTenant) {
    return <div className="software-container"><h2>No tenant selected</h2></div>;
  }

  if (loading) {
    return (
      <div className="software-container">
        <div className="software-header">
          <h2>License Assignments</h2>
        </div>
        <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="software-container">
        <div className="software-header"><h2>License Assignments</h2></div>
        <div className="error-container"><div className="error-icon">⚠️</div><h3>Error</h3><p>{error}</p></div>
      </div>
    );
  }


  // Default: group by Description + UPN (parent), expand to show service plans (children)
  const grouped = rows.reduce((acc, r) => {
    const key = `${r.description}||${r.upn}`;
    if (!acc[key]) acc[key] = { description: r.description, upn: r.upn, skuPartNumber: r.skuPartNumber, children: [] };
    acc[key].children.push({ servicePlanName: r.servicePlanName });
    return acc;
  }, {});
  const parentList = Object.values(grouped).filter(p => {
    const s = search.toLowerCase();
    if (!s) return true;
    return (
      p.description?.toLowerCase().includes(s) ||
      p.skuPartNumber?.toLowerCase().includes(s) ||
      p.upn?.toLowerCase().includes(s)
    );
  });
  return (
    <div className="software-container">
      <div className="software-header">
        <h2>License Assignments</h2>
        <div className="software-actions">
          <button
            onClick={() => window.dispatchEvent(new Event('navigateToAnalytics'))}
            className="clear-filters-button"
            style={{ marginRight: 8 }}
          >
            ← Back to Analytics
          </button>
          <input
            type="text"
            placeholder="Search description or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="column-search-input"
            style={{ width: 280 }}
          />
          <button
            onClick={() => setSearch('')}
            className="clear-filters-button"
            style={{ marginLeft: 8 }}
            disabled={!search}
          >
            Clear Filter
          </button>
          <span className="software-count">{parentList.length} users</span>
        </div>
      </div>

      <div className="software-grid-container">
        <div className="software-grid">
          <table className="software-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>UPN</th>
              </tr>
            </thead>
            <tbody>
              {parentList.map((p, i) => (
                <React.Fragment key={i}>
                  <tr className="table-row" onClick={() => setExpandedSku(expandedSku === p.upn ? null : p.upn)} style={{ cursor: 'pointer' }}>
                    <td className="table-cell">{expandedSku === p.upn ? '▼ ' : '▶ '} {p.description}</td>
                    <td 
                      className="table-cell clickable-cell" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Navigate to Assets page with UPN filter
                        const evt = new CustomEvent('navigateToAssets', { detail: { upn: p.upn } });
                        window.dispatchEvent(evt);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      {p.upn}
                    </td>
                  </tr>
                  {expandedSku === p.upn && (
                    <tr className="table-row">
                      <td className="table-cell" colSpan={2}>
                        <ChildPlans plans={p.children} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ChildPlans = ({ plans }) => {
  return (
    <table className="software-table">
      <thead>
        <tr>
          <th>Service Plan</th>
        </tr>
      </thead>
      <tbody>
        {plans.map((p, idx) => (
          <tr key={idx} className="table-row">
            <td className="table-cell">{p.servicePlanName}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default LicenseGrid;


